import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import profilecross from "../../../assets/profilecross.svg";
import CustomText, { textStyles } from "../../../components/CustomText";
import code from "../../../assets/code.svg";
import toggle from "../../../assets/toggle.svg";
import toggleoff from "../../../assets/Settings/toggleoff.svg";
import { Drawer, Box, IconButton, CircularProgress } from "@mui/material";
import StatusDropdown from "../../../components/StatusDropdown";
import { useTranslation } from "react-i18next";
import { BASE_URL } from "../../../components/BaseUrl";
import { decryptToken } from "../../../Utlis/Encryption";
import down from "../../../assets/down.svg";
import dummy from "../../../assets/dummy.png";

const ProfileDrawer = ({ open, onClose }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const token = useSelector((state) => state.auth.token);
  const decryptedToken = token ? decryptToken(token) : null;

  const [profile, setProfile] = useState(null);
  const [verificationStatus, setVerificationStatus] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [loading, setLoading] = useState(false);

  // ✅ Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return "--";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  useEffect(() => {
    if (!open) return; // only fetch when drawer is open

    setLoading(true);
    fetch(`${BASE_URL}/company-admins/profile`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${decryptedToken}`,
      },
    })
      .then((res) => res.json())
      .then((res) => {
        console.log("==== 🔎 COMPANY ADMIN PROFILE API RESPONSE ====");
        console.log("Raw Response Object:", res);
        console.log("Pretty JSON Response:", JSON.stringify(res, null, 2));
        console.log("===============================================");

        if (!res.error && res.data) {
          // Merge all sections into one profile object
          const mergedProfile = {
            ...res.data.user,
            ...res.data.company,
            ...res.data.location,
            ...res.data.coordinates,
            ...res.data.territory_zone,

            ...res.data.account_executive,
            ...res.data.status,
          };

          setProfile(mergedProfile);
          setVerificationStatus(res.data.status?.verification_status || "");
        } else {
          toast.error(res.message || "Failed to fetch profile");
        }
      })
      .catch((err) => {
        console.error("❌ Fetch company-admin profile error:", err);
        toast.error("Something went wrong while fetching profile");
      })
      .finally(() => setLoading(false));
  }, [open, decryptedToken]);

//   const capitalizeWords = (str) => {
//   return str
//     .split('_')  // Split by underscore
//     .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()) // Capitalize each word
//     .join(' ');  // Join words back with space
// };

const capitalizeWords = (str) => {
  if (!str || typeof str !== "string") return "--";

  return str
    .split("_")
    .map(
      word =>
        word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    )
    .join(" ");
};


  const ChipList = ({ items }) => {
    const safeItems = Array.isArray(items)
      ? items.filter(Boolean)
      : items
      ? [items]
      : [];


    return (
      <Box
        sx={{
          display: "flex",
          gap: 1,
          flexWrap: "wrap",
          justifyContent: "flex-end",
        }}
      >
        {safeItems.length > 0 ? (
          safeItems.map((item, i) => (
            <Box
              key={`${item}-${i}`}
              sx={{
                backgroundColor: "#579DFF",
                color: "#09326C",
                px: 1.5,
                py: 0.6,
                borderRadius: "4px",
                fontSize: "14px",
                fontWeight: 400,
                fontFamily: "Poppins, sans-serif",
                maxWidth: 220,
                textOverflow: "ellipsis",
                overflow: "hidden",
                whiteSpace: "nowrap",
              }}
              title={String(item)}
            >
              {String(item)}
            </Box>
          ))
        ) : (
          <CustomText sx={{ fontSize: 14, color: "#999" }}>--</CustomText>
        )}
      </Box>
    );
  };

  const getProfileImageSrc = (raw) => {
    if (!raw) return dummy;
    if (raw.startsWith("data:")) return raw;
    if (/^https?:\/\//i.test(raw)) return raw;
    const prefix = raw.startsWith("/") ? "" : "/";
    return `${BASE_URL}${prefix}${raw}`;
  };


  const formatAdminType = (value) => {
  if (!value) return "--";

  return value
    .replace(/_/g, " ")           // replace underscores with spaces
    .toLowerCase()                // convert to lowercase
    .replace(/\b\w/g, (c) => c.toUpperCase()); // capitalize each word
};

const capitalizeText = (text) => {
  if (!text) return text;
  return text
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};
  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box
        sx={{
          width: 560,
          maxWidth: "100vw",
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 10,
            backgroundColor: "#E7EBEE",
            p: 1.3,
            borderBottom: "1px solid #eee",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              gap: 1,
            }}
          >
            <IconButton onClick={onClose}>
              <img src={profilecross} alt="close" />
            </IconButton>
            <CustomText
              sx={{
                ...textStyles.h1,
                fontSize: 22,
                fontWeight: 600,
                color: "#2C384C",
              }}
            >
              {t("profileDrawer.profileTitle")}
            </CustomText>
          </Box>
        </Box>

        {/* Body */}
        <Box sx={{ overflowY: "auto", flexGrow: 1, p: 2 }}>
          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
              }}
            >
              <CircularProgress />
            </Box>
          ) : profile ? (
            <>
              {/* Personal Details */}
              <CustomText
                sx={{
                  ...textStyles.h1,
                  fontSize: 18,
                  mb: 3,
                  mt: 1,
                  fontWeight: 600,
                  color: "#000000",
                  textAlign: "left",
                }}
              >
                {t("profileDrawer.personalDetails")}
              </CustomText>

              <Box
                p={2}
                sx={{
                  pt: 2,
                  mt: 2,
                  backgroundColor: "#fff",
                  border: "1px solid #091E4224",
                  borderRadius: "10px",
                }}
              >
                <Box sx={{ textAlign: "center" }}>
                  <img
                    crossOrigin="anonymous"
                    src={getProfileImageSrc(profile?.profile_picture)}
                    alt="avatar"
                    style={{
                      width: 150,
                      height: 150,
                      objectFit: "cover",
                      borderRadius: "50%",
                      border: "1px solid #E5E7EB",
                    }}
                    onError={(e) => (e.currentTarget.src = dummy)}
                  />
                </Box>

                <Box mt={2}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 1.5,
                    }}
                  >
                    <CustomText
                      sx={{
                        ...textStyles.h1,
                        fontSize: 16,
                        fontWeight: 400,
                        color: "#5E5C5C",
                      }}
                    >
                      {t("profileDrawer.status")}
                    </CustomText>

                    <StatusDropdown
                      currentStatus={verificationStatus}
                      onChange={(newStatus) =>
                        setVerificationStatus(newStatus)
                      }
                    />
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.5,
                    }}
                  >
                    <InfoRow
                      label={t("profileDrawer.registeredOn")}
                      value={formatDate(profile.created_at)}
                    />
                    <InfoRow
                      label={t("profileDrawer.fullName")}
                      value={profile.full_name}
                    />
                    <InfoRow
                      label={t("profileDrawer.phone")}
                      value={profile.phone_number}
                    />
                
                <InfoRow
  label={t("profileDrawer.dob")}
  value={formatDate(profile.dob)}
/>

                  </Box>
                </Box>
              </Box>

              {/* Address Section */}
              <SectionHeader
                label={t("profileDrawer.addressDetails")}
                icon={down}
              />
              <InfoBox>
                <InfoRow
                  label={t("profileDrawer.country")}
                  value={profile.country}
                />
                <InfoRow
                  label={t("profileDrawer.province")}
                  value={profile.province}
                />
                <InfoRow
                  label={t("profileDrawer.city")}
                  value={profile.city}
                />
                <InfoRow
                  label={t("profileDrawer.postalCode")}
                  value={profile.postal_code}
                />
                <InfoRow
                  label={t("profileDrawer.streetAddress")}
                  value={profile.street_address}
                />
              </InfoBox>

              {/* Executive Section */}
              <SectionHeader
                label={t("profileDrawer.executiveDetails")}
                icon={down}
              />
              <InfoBox>
             
<InfoRow label={t("executiveName")} value={profile.name} />
<InfoRow label={t("executiveEmail")} value={profile.email} />

                {/* <InfoRow
                  label={t("profileDrawer.activeForService")}
                  value={
                    <img
                      src={isActive ? toggle : toggleoff}
                      alt="toggle"
                      style={{ cursor: "pointer", width: 40 }}
                      onClick={() => setIsActive((prev) => !prev)}
                    />
                  }
                /> */}
              </InfoBox>

              {/* Business Section */}
              <SectionHeader
                label={t("profileDrawer.businessDetails")}
                icon={down}
              />
              <InfoBox>
                <InfoRow
                  label={t("profileDrawer.businessSector")}
                   value={capitalizeText(profile.business_sector)}
                />
              <InfoRow
  label={t("profileDrawer.businessType")}
  value={capitalizeWords(profile.business_type)}
/>
                {/* <InfoRow
                  label={t("profileDrawer.tradeName")}
                  value={profile.trade_name}
                /> */}
                <InfoRow
                  label={t("profileDrawer.legalName")}
                  value={profile.legal_name}
                />
                <InfoRow
                  label={t("profileDrawer.businessEmail")}
                  value={profile.business_email}
                />
                <InfoRow
                  label={t("profileDrawer.businessPhone")}
                  value={profile.business_phone}
                />
                  <InfoRow
    label={t("profileDrawer.administratorType")}
  value={formatAdminType(profile.administrator_type)}

  />
    <InfoRow
    label={t("profileDrawer.businessActivity")}
    value={profile.business_activity}
  />
    <InfoRow
    label={t("profileDrawer.primaryColor")}
    value={
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box sx={{
          width: 18, height: 18, borderRadius: "50%",
          backgroundColor: profile.primary_color, border: "1px solid #ccc"
        }} />
        {profile.primary_color}
      </Box>
    }
  />
  
  <InfoRow
    label={t("profileDrawer.secondaryColor")}
    value={
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box sx={{
          width: 18, height: 18, borderRadius: "50%",
          backgroundColor: profile.secondary_color, border: "1px solid #ccc"
        }} />
        {profile.secondary_color}
      </Box>
    }
  />
              </InfoBox>

              {/* Region Section */}
              {/* <SectionHeader
                label={t("profileDrawer.selectedRegion")}
                icon={down}
              />
              <InfoBox>
                <InfoRow
                  label={t("profileDrawer.zone")}
                  value={<ChipList items={profile.territory_zone} />}
                />
              </InfoBox> */}
            </>
          ) : (
            <CustomText sx={{ textAlign: "center", mt: 4 }}>
              No Profile Data
            </CustomText>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};

const InfoRow = ({ label, value }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      mb: 2,
    }}
  >
    <CustomText
      sx={{
        ...textStyles.h1,
        fontSize: 16,
        fontWeight: 400,
        color: "#5E5C5C",
      }}
    >
      {label}
    </CustomText>
    <CustomText
      sx={{
        ...textStyles.h1,
        fontSize: 15,
        fontWeight: 400,
        color: "#172B4D",
        textAlign: "right",
      }}
    >
      {value || "--"}
    </CustomText>
  </Box>
);

const SectionHeader = ({ label, icon }) => (
  <Box mt={2.5} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
    <CustomText
      sx={{ ...textStyles.h1, fontSize: 18, fontWeight: 600, color: "#000000" }}
    >
      {label}
    </CustomText>
    {/* <img src={icon} width={20} height={20} alt="section icon" /> */}
  </Box>
);

const InfoBox = ({ children }) => (
  <Box
    mt={2}
    sx={{
      border: "1px solid #091E4224",
      borderRadius: "10px",
      px: 2,
      py: 2,
    }}
  >
    {children}
  </Box>
);

export default ProfileDrawer;












